/**
 * §06 / §04 — Projectile integration, swept collision and hit resolution.
 *
 * Owns every spawned combat entity that is not an enemy: linear shots, homing barbs, orbitals,
 * beams, lobbed mines, damage zones, chain arcs, minions, radial auras and melee cones.
 *
 * Determinism: iteration is always `pool.liveIndices()` ascending (§14.3); the only RNG consumed
 * is `w.rng.crit`, and only through `weapons.ts` at fire time (§4.11). Collision is a swept
 * segment test through the spatial hash so nothing tunnels at any speed (§14.4).
 *
 * Damage math is NOT implemented here — every hit funnels through `applyDamageToEnemy`
 * (R1, `src/sim/damage.ts`) so the §4.1 pipeline has exactly one implementation.
 */

import { cfg } from '../core/config';
import type { HitContact } from '../core/events';
import { applyDamageToEnemy, applyKnockbackToEnemy } from '@/sim/damage';
import {
  sweptCircleWrapped,
  wrapDeltaX,
  wrapDeltaY,
  wrapPos,
  wrapSegDist2,
  wrapX,
  wrapY,
} from './wrap';
import { applyStatus } from './status';
import type { Enemy, Projectile } from './entities';
import { POOL_CAPACITY, type World } from './world';
import {
  MAX_ACQUISITION_RANGE,
  POLICY_ELITE_PRIORITY,
  POLICY_NEAREST,
  VISUAL_CLASS_BY_WEAPON,
  acquireTargetByCode,
  aimAt,
  aimOut,
  enemyByEntityId,
  isTargetable,
  isWithinVisibleRange,
  rebuildEnemyIndex,
  weaponRuntime,
  type WeaponRuntime,
} from './targeting';

export { VISUAL_CLASS_BY_WEAPON };

/** Behaviour selector, shared with R6 via `projectile.behaviour` (INTERFACES.md). */
export const enum Behaviour {
  Linear = 0,
  Homing = 1,
  Orbital = 2,
  Beam = 3,
  Lobbed = 4,
  Zone = 5,
  Chain = 6,
  Minion = 7,
  Aura = 8,
  Melee = 9,
  Mine = 10,
  Ricochet = 11,
}

export const STEP_S = 1 / 60;
const CAP = POOL_CAPACITY.projectiles;

/** Visual classes 12–14 are reserved for enemy projectiles (§14.2). */
export const VISUAL_CLASS_ENEMY_BASE = 12;

// --- per-projectile side state (preallocated; §14.3 zero-alloc) --------------------------------

/** Owning weapon slot (0..3); -1 = active slot. */
const pSlot = new Int32Array(CAP);
/** Next tick at which this projectile applies damage (beams, zones, orbital flares). */
const pTimer = new Int32Array(CAP);
/** Arm tick (mines) / dive-ready tick (minions) / re-arc tick (chain nodes). */
const pArm = new Int32Array(CAP);
/** Behaviour adopted when a Lobbed projectile reaches its destination. */
const pNext = new Int32Array(CAP);
/** Generic float slot: beam ramp seconds, zone base radius, minion dive progress. */
const pAux = new Float64Array(CAP);
/** Destination / anchor point (lobbed transit, zone centre, chain node origin). */
const pDestX = new Float64Array(CAP);
const pDestY = new Float64Array(CAP);
/** Per-enemy hit-cooldown expiry, parallel to `projectile.hit_ids` (16 slots each). */
const HIT_SLOTS = 16;
const pHitTick = new Int32Array(CAP * HIT_SLOTS);
/**
 * §6.6 — shards this body still owes on death (passive.mitotic-bloom), and cascade levels it
 * still owes on its first strike (passive.cascade-ganglion). Both are one-shot charges, and
 * both are seeded from `spawnStats`, which means the generation cap is structural: a shard is
 * spawned through `resetSpawnStats`, so it carries 0 of each and can never split or cascade
 * again. Nothing in the codebase can hand a second-generation body a charge without saying so.
 */
const pSplit = new Int32Array(CAP);
const pCascade = new Int32Array(CAP);

// --- shared scratch ----------------------------------------------------------------------------

/**
 * Hash queries are padded by this margin because the spatial hash buckets an entity by its own
 * cell only (radius ≤ 2 u): an enemy whose disc overlaps the query but whose centre sits in the
 * neighbouring cell would otherwise never become a candidate. 2 u = half a cell, which covers
 * every non-boss hitbox; bosses insert into every cell they overlap and are covered anyway.
 */
const HIT_PAD = 2;

const segBuf = new Int32Array(1024);
const areaBuf = new Int32Array(1024);
const hitT = new Float64Array(64);
const hitPi = new Int32Array(64);
const chainVisited = new Int32Array(32);
/** §6.6 cascade frontier: the ≤ 10 nearest unvisited bodies, sorted by squared distance. */
const CASCADE_MAX_NODES = 16;
const cascPi = new Int32Array(CASCADE_MAX_NODES);
const cascD2 = new Float64Array(CASCADE_MAX_NODES);
const tauntId = new Int32Array(64);
const tauntUntil = new Int32Array(64);
const tauntX = new Float64Array(64);
const tauntY = new Float64Array(64);
let tauntCount = 0;

/** Spawn parameters. Callers fill the shared `spawnStats` instance — never a fresh literal. */
export interface ProjectileSpawnStats {
  damage: number;
  crit: boolean;
  speed: number;
  /** Collision radius of the projectile body. */
  radius: number;
  /** Payload radius: blast / zone / pulse / orbit sample radius. */
  area: number;
  penetration: number;
  ricochet: number;
  duration_s: number;
  knockback: number;
  behaviour: number;
  visual_class: number;
  homing: number;
  orbit_radius: number;
  phase: number;
  target_id: number;
  status_on_hit: string;
  slot: number;
  next_behaviour: number;
  dest_x: number;
  dest_y: number;
  arm_s: number;
  /** Damage-tick interval in seconds for beams / zones / orbitals. */
  tick_s: number;
  /** §6.6 — shards this body bursts into when it is spent (0 = no split). */
  split: number;
  /** §6.6 — cascade levels this body spends on its first strike (0 = no cascade). */
  cascade: number;
}

export const spawnStats: ProjectileSpawnStats = {
  damage: 0,
  crit: false,
  speed: 0,
  radius: 0.35,
  area: 0,
  penetration: 1,
  ricochet: 0,
  duration_s: 1,
  knockback: 0,
  behaviour: Behaviour.Linear,
  visual_class: 0,
  homing: 0,
  orbit_radius: 0,
  phase: 0,
  target_id: 0,
  status_on_hit: '',
  slot: 0,
  next_behaviour: Behaviour.Linear,
  dest_x: 0,
  dest_y: 0,
  arm_s: 0,
  tick_s: 0,
  split: 0,
  cascade: 0,
};

/** Reset `spawnStats` to neutral defaults before filling it for a new weapon. */
export function resetSpawnStats(): void {
  spawnStats.damage = 0;
  spawnStats.crit = false;
  spawnStats.speed = 0;
  spawnStats.radius = 0.35;
  spawnStats.area = 0;
  spawnStats.penetration = 1;
  spawnStats.ricochet = 0;
  spawnStats.duration_s = 1;
  spawnStats.knockback = 0;
  spawnStats.behaviour = Behaviour.Linear;
  spawnStats.visual_class = 0;
  spawnStats.homing = 0;
  spawnStats.orbit_radius = 0;
  spawnStats.phase = 0;
  spawnStats.target_id = 0;
  spawnStats.status_on_hit = '';
  spawnStats.slot = 0;
  spawnStats.next_behaviour = Behaviour.Linear;
  spawnStats.dest_x = 0;
  spawnStats.dest_y = 0;
  spawnStats.arm_s = 0;
  spawnStats.tick_s = 0;
  spawnStats.split = 0;
  spawnStats.cascade = 0;
}

// --- spawn --------------------------------------------------------------------------------------

/**
 * Acquire and initialize a projectile. Returns null when the pool is exhausted; the caller
 * must drop the fire event after `pool_exhausted` is emitted here (§14.3 deterministic denial).
 */
export function spawnProjectile(
  w: World,
  weaponId: string,
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  stats: ProjectileSpawnStats,
  ownerId: number
): Projectile | null {
  // §14.1 B3 — the alive cap is the budget, not the pool. The pool carries 36 slots of
  // margin over the 1,500 cap for in-flight bookkeeping, so guarding only on `acquire()`
  // would let the live count reach 1,536. It also makes AMD-2's per-tier caps bind: on
  // `handheld` the budget is 450 while the pool is still 1,536.
  if (w.projectiles.count >= cfg.get('budget.max_projectiles')) {
    w.bus.emit('pool_exhausted', { pool: 'projectiles', denied_kind: weaponId });
    return null;
  }
  const p = w.projectiles.acquire();
  if (p === null) {
    w.bus.emit('pool_exhausted', { pool: 'projectiles', denied_kind: weaponId });
    return null;
  }
  const i = p.pool_index;
  p.id = w.allocId();
  p.weapon_id = weaponId;
  p.owner_id = ownerId;
  p.faction = 'player';
  // AMD-24 — spawn sites hand us muzzle offsets in the caller's local frame (player position
  // ± a lance offset, an orbit radius, a scatter ring). Canonicalising here is the single point
  // where a projectile enters the fundamental domain, so no caller can leak an off-torus body.
  p.pos.x = wrapX(x);
  p.pos.y = wrapY(y);
  p.vel.x = dirX * stats.speed;
  p.vel.y = dirY * stats.speed;
  p.hp = 1;
  p.max_hp = 1;
  p.hitbox_radius = stats.radius;
  p.targetable = false;
  p.spawn_tick = w.tick;
  p.spawn_tick_f = w.tick;
  p.damage_snapshot = stats.damage;
  p.crit_snapshot = stats.crit;
  p.area_snapshot = stats.area;
  p.knockback_snapshot = stats.knockback;
  p.penetration_left = stats.penetration;
  p.ricochet_left = stats.ricochet;
  p.expire_tick = stats.duration_s < 0 ? 0x7fffffff : w.tick + Math.max(1, Math.round(stats.duration_s * 60));
  p.hit_count = 0;
  p.behaviour = stats.behaviour;
  p.phase = stats.phase;
  p.orbit_radius = stats.orbit_radius;
  p.homing_strength = stats.homing;
  p.target_id = stats.target_id;
  p.status_on_hit = stats.status_on_hit;
  p.visual_class = stats.visual_class;
  p.status_count = 0;

  pSlot[i] = stats.slot;
  pNext[i] = stats.next_behaviour;
  pDestX[i] = wrapX(stats.dest_x);
  pDestY[i] = wrapY(stats.dest_y);
  pAux[i] = 0;
  pSplit[i] = stats.split;
  pCascade[i] = stats.cascade;
  pArm[i] = stats.arm_s > 0 ? w.tick + Math.round(stats.arm_s * 60) : w.tick;
  // Damage-tick shapes land their first tick on the spawn tick, so a 5 Hz beam delivers
  // exactly 5 ticks in its first second (§6.3.3 acceptance measures sustained DPS from t=0).
  pTimer[i] = w.tick;
  const base = i * HIT_SLOTS;
  for (let k = 0; k < HIT_SLOTS; k++) {
    p.hit_ids[k] = 0;
    pHitTick[base + k] = 0;
  }
  return p;
}

/** Owning weapon slot of a live projectile (0..3, -1 = active slot). */
export function projectileSlot(p: Projectile): number {
  return pSlot[p.pool_index];
}

/** Zone / mine anchor point, read by R6 for placement visuals. */
export function projectileAnchorX(p: Projectile): number {
  return pDestX[p.pool_index];
}
export function projectileAnchorY(p: Projectile): number {
  return pDestY[p.pool_index];
}

// --- damage plumbing -------------------------------------------------------------------------

/**
 * Single funnel into R1's §4.1 pipeline (`src/sim/damage.ts`). `amount` is the fire-time
 * snapshot: base damage × the player's attacker-side multipliers (§4.1 steps 1–2). The damage
 * module applies Lumensurge, the target's damage-taken bonus, the crit roll, mitigation and
 * rounding.
 *
 * `canCrit` is false only when the crit was already rolled and baked into `amount`
 * (evolution.kingspear-medusa needs the crit result at spawn time to fragment into barbs),
 * so exactly one `crit`-stream roll is consumed per hit event either way (§4.11).
 *
 * `contact` is presentation-only and states, per call site, whether this shape has a contact
 * point at all. A swept bolt does — it is the point on the rim the sweep resolved to. A zone
 * tick, a beam sheet, an orbiter grinding along a body and an aura ring do not: they touch a
 * body over an area or over time, and there is no single place the blow "landed", so they
 * pass `null` and the beat is drawn at the body centre.
 */
function dealDamage(
  w: World,
  target: Enemy,
  amount: number,
  weaponId: string,
  canCrit: boolean,
  sourceId: number,
  depth: number,
  contact: HitContact | null
): number {
  if (amount <= 0) return 0;
  return applyDamageToEnemy(w, target, amount, weaponId, sourceId, canCrit, depth, contact);
}

/**
 * Scratch contact record for the swept-bolt path. `commitEnemyDamage` copies it into the
 * event ring, so one instance serves every hit in a tick and nothing allocates (§14.3).
 */
const bolt: HitContact = { x: 0, y: 0, dir_x: 0, dir_y: 0 };

/** Weaken-family application used by Alarm Mark, Eclipse and the brine-lake amp (§04 step 3). */
function applyWeakenFamily(w: World, e: Enemy, pct: number, sourceId: number): void {
  if (pct <= 0) return;
  applyStatus(w, e, 'status.weaken', 1, sourceId, pct / 100);
}

// --- hit bookkeeping --------------------------------------------------------------------------

function alreadyHit(p: Projectile, enemyId: number, tick: number): boolean {
  const base = p.pool_index * HIT_SLOTS;
  const n = p.hit_count < HIT_SLOTS ? p.hit_count : HIT_SLOTS;
  for (let i = 0; i < n; i++) {
    if (p.hit_ids[i] === enemyId) return pHitTick[base + i] > tick;
  }
  return false;
}

function markHit(p: Projectile, enemyId: number, untilTick: number): void {
  const base = p.pool_index * HIT_SLOTS;
  const n = p.hit_count < HIT_SLOTS ? p.hit_count : HIT_SLOTS;
  for (let i = 0; i < n; i++) {
    if (p.hit_ids[i] === enemyId) {
      pHitTick[base + i] = untilTick;
      return;
    }
  }
  const slot = p.hit_count % HIT_SLOTS;
  p.hit_ids[slot] = enemyId;
  pHitTick[base + slot] = untilTick;
  p.hit_count++;
}

// --- area helpers (exported: weapons.ts resolves instant strikes through these) ----------------

/**
 * Radial strike. Damages every targetable enemy whose centre is within `radius` of (x,y).
 * Returns the number of enemies struck. One crit result per strike event (§4.1 AoE rule).
 */
export function radialDamage(
  w: World,
  x: number,
  y: number,
  radius: number,
  damage: number,
  weaponId: string,
  canCrit: boolean,
  sourceId: number,
  knockback: number,
  depth: number
): number {
  rebuildEnemyIndex(w);
  const n = w.hash.queryCircle(x, y, radius + HIT_PAD, areaBuf, areaBuf.length);
  const stamp = w.nextStamp();
  const r2 = radius * radius;
  let struck = 0;
  for (let i = 0; i < n; i++) {
    const pi = areaBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    const dx = wrapDeltaX(e.pos.x - x);
    const dy = wrapDeltaY(e.pos.y - y);
    const d2 = dx * dx + dy * dy;
    const reach = radius + e.hitbox_radius;
    if (d2 > reach * reach && d2 > r2) continue;
    dealDamage(w, e, damage, weaponId, canCrit, sourceId, depth, null);
    if (knockback > 0) {
      const d = Math.sqrt(d2) || 1;
      applyKnockbackToEnemy(w, e, dx, dy, knockback);
    }
    struck++;
  }
  return struck;
}

/**
 * Cone strike (§6.3.11 gulper maw). `halfAngleDeg` is half of the weapon's cone stat.
 * Returns the number of enemies struck; `outExecuted` counts sub-threshold non-elites when
 * `executePct > 0` (evolution.gulper-worldmaw).
 */
export function coneDamage(
  w: World,
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  radius: number,
  coneDeg: number,
  damage: number,
  weaponId: string,
  canCrit: boolean,
  sourceId: number,
  executePct: number
): number {
  rebuildEnemyIndex(w);
  const n = w.hash.queryCircle(x, y, radius + HIT_PAD, areaBuf, areaBuf.length);
  const stamp = w.nextStamp();
  const cosLimit = Math.cos((coneDeg * 0.5 * Math.PI) / 180);
  let executed = 0;
  for (let i = 0; i < n; i++) {
    const pi = areaBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    const dx = wrapDeltaX(e.pos.x - x);
    const dy = wrapDeltaY(e.pos.y - y);
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d > radius + e.hitbox_radius) continue;
    if (d > 1e-6 && (dx / d) * dirX + (dy / d) * dirY < cosLimit) continue;
    // §6.3.11 evolution — execute non-elites strictly below the HP threshold.
    if (executePct > 0 && !e.elite && !e.is_boss && e.hp / e.max_hp < executePct / 100) {
      dealDamage(w, e, e.hp + e.max_hp, weaponId, canCrit, sourceId, 0, null);
      executed++;
      continue;
    }
    dealDamage(w, e, damage, weaponId, canCrit, sourceId, 0, null);
  }
  return executed;
}

/**
 * §6.3.7 — chain arc. Damages the primary target then jumps to the nearest unvisited enemy
 * within `linkRange`, applying `falloffPct` per link. Returns the number of enemies struck.
 * Never revisits a target inside one arc (§6.3.7 acceptance).
 */
export function resolveChain(
  w: World,
  startId: number,
  damage: number,
  links: number,
  linkRange: number,
  falloffPct: number,
  weaponId: string,
  canCrit: boolean,
  sourceId: number,
  visitedSeed: number,
  depth: number
): number {
  rebuildEnemyIndex(w);
  let current = enemyByEntityId(w, startId);
  if (current === null) return 0;
  let visited = 0;
  if (visitedSeed !== 0) {
    chainVisited[visited++] = visitedSeed;
  }
  let dmg = damage;
  const maxLinks = links < chainVisited.length - 1 ? links : chainVisited.length - 1;
  let struck = 0;
  for (let link = 0; link < maxLinks; link++) {
    chainVisited[visited++] = current.id;
    dealDamage(w, current, dmg, weaponId, canCrit, sourceId, depth, null);
    struck++;
    // spawn a visual arc segment (no collision) from the previous node to this one
    spawnChainSegment(w, weaponId, current.pos.x, current.pos.y);
    dmg *= 1 - falloffPct / 100;
    const next = nearestUnvisited(w, current.pos.x, current.pos.y, linkRange, visited);
    if (next === null) break;
    current = next;
  }
  return struck;
}

function nearestUnvisited(w: World, x: number, y: number, range: number, visitedCount: number): Enemy | null {
  const n = w.hash.queryCircle(x, y, range + HIT_PAD, areaBuf, areaBuf.length);
  const stamp = w.nextStamp();
  let best: Enemy | null = null;
  let bestD2 = Infinity;
  let bestId = 0;
  const r2 = range * range;
  for (let i = 0; i < n; i++) {
    const pi = areaBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    let seen = false;
    for (let v = 0; v < visitedCount; v++) {
      if (chainVisited[v] === e.id) {
        seen = true;
        break;
      }
    }
    if (seen) continue;
    // §5.8 — a link hop picks a NEW target, so it obeys the on-screen clamp: a chain that
    // starts on a visible enemy may not walk itself off the frame link by link.
    if (!isWithinVisibleRange(w, e.pos.x, e.pos.y)) continue;
    // §6.3.7 — a link may jump the seam: the arc reaches the nearest image of the candidate.
    const dx = wrapDeltaX(e.pos.x - x);
    const dy = wrapDeltaY(e.pos.y - y);
    const d2 = dx * dx + dy * dy;
    if (d2 > r2) continue;
    if (d2 < bestD2 - 1e-9 || (Math.abs(d2 - bestD2) <= 1e-9 && e.id < bestId)) {
      bestD2 = d2;
      best = e;
      bestId = e.id;
    }
  }
  return best;
}

function spawnChainSegment(w: World, weaponId: string, x: number, y: number): void {
  resetSpawnStats();
  spawnStats.behaviour = Behaviour.Chain;
  spawnStats.visual_class = VISUAL_CLASS_BY_WEAPON[weaponId] ?? 0;
  spawnStats.duration_s = 0.12;
  spawnStats.radius = 0.2;
  spawnStats.penetration = 0;
  spawnProjectile(w, weaponId, x, y, 1, 0, spawnStats, w.player.id);
}

// --- taunt (evolution.angler-grand-lure) --------------------------------------------------------

/** §6.3.6 evolution — pull non-elites toward the lure by overriding their steering goal. */
export function applyTaunt(w: World, x: number, y: number, radius: number, durationS: number): void {
  const n = w.hash.queryCircle(x, y, radius, areaBuf, areaBuf.length);
  const stamp = w.nextStamp();
  const until = w.tick + Math.round(durationS * 60);
  const r2 = radius * radius;
  for (let i = 0; i < n; i++) {
    const pi = areaBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    if (e.elite || e.is_boss) continue; // never taunts elites/bosses (acceptance)
    const dx = wrapDeltaX(e.pos.x - x);
    const dy = wrapDeltaY(e.pos.y - y);
    if (dx * dx + dy * dy > r2) continue;
    let found = -1;
    for (let k = 0; k < tauntCount; k++) {
      if (tauntId[k] === e.id) {
        found = k;
        break;
      }
    }
    if (found < 0) {
      if (tauntCount >= tauntId.length) continue;
      found = tauntCount++;
      tauntId[found] = e.id;
    }
    tauntUntil[found] = until;
    tauntX[found] = x;
    tauntY[found] = y;
  }
}

function stepTaunts(w: World): void {
  let write = 0;
  for (let i = 0; i < tauntCount; i++) {
    if (tauntUntil[i] <= w.tick) continue;
    const e = enemyByEntityId(w, tauntId[i]);
    if (e === null) continue;
    e.steering.goal.x = tauntX[i];
    e.steering.goal.y = tauntY[i];
    tauntId[write] = tauntId[i];
    tauntUntil[write] = tauntUntil[i];
    tauntX[write] = tauntX[i];
    tauntY[write] = tauntY[i];
    write++;
  }
  tauntCount = write;
}

// --- per-behaviour integration -------------------------------------------------------------------

function runtimeOf(w: World, p: Projectile): WeaponRuntime | null {
  const s = pSlot[p.pool_index];
  if (s < 0 || s > 3) return null;
  const rt = weaponRuntime(w, s);
  return rt.valid && rt.def_id === p.weapon_id ? rt : null;
}

/**
 * Swept-segment integration + hit resolution for Linear / Homing / Ricochet bodies.
 *
 * AMD-24 — the tick is resolved in the projectile's own locally-Euclidean frame: `dx,dy` is the
 * true motion taken from the velocity, never `x1 - x0` of two canonicalised positions (that
 * would read as a 200 u jump the frame a body crosses the seam). The unwrapped end point is
 * what the toroidal DDA in `querySegment` wants, and each candidate is narrowed with
 * `sweptCircleWrapped`, which re-expresses the enemy as its image nearest the segment origin.
 * Canonicalisation happens once, after the sweep, so the swept test and the hash walk agree.
 */
function stepMoving(w: World, p: Projectile): void {
  const x0 = p.pos.x;
  const y0 = p.pos.y;
  const dx = p.vel.x * STEP_S;
  const dy = p.vel.y * STEP_S;
  const x1 = x0 + dx;
  const y1 = y0 + dy;

  const n = w.hash.querySegment(x0, y0, x1, y1, p.hitbox_radius + HIT_PAD, segBuf, segBuf.length);
  const stamp = w.nextStamp();
  let hits = 0;
  for (let i = 0; i < n && hits < hitT.length; i++) {
    const pi = segBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    if (alreadyHit(p, e.id, w.tick)) continue;
    const t = sweptCircleWrapped(x0, y0, dx, dy, p.hitbox_radius, e.pos.x, e.pos.y, e.hitbox_radius);
    if (t < 0) continue;
    // insertion sort by time of impact — penetration must consume targets in contact order
    let k = hits;
    while (k > 0 && (hitT[k - 1] > t || (hitT[k - 1] === t && w.enemies.slots[hitPi[k - 1]].id > e.id))) {
      hitT[k] = hitT[k - 1];
      hitPi[k] = hitPi[k - 1];
      k--;
    }
    hitT[k] = t;
    hitPi[k] = pi;
    hits++;
  }

  p.pos.x = x1;
  p.pos.y = y1;
  wrapPos(p.pos);

  for (let i = 0; i < hits; i++) {
    const e = w.enemies.slots[hitPi[i]];
    if (!isTargetable(e)) continue;
    if (!onProjectileHit(w, p, e, hitT[i])) return;
  }
  // AMD-24 — nothing leaves a torus, so there is no out-of-bounds despawn: a body that crosses
  // the seam keeps flying and still hits. Range and lifetime are the only budgets that end it
  // (`expire_tick`, checked in `stepProjectiles`).
}

// --- §6.6 split and cascade ---------------------------------------------------------------------

/**
 * Shard damage as a fraction of the parent's snapshot. Four shards at 35% is 1.4× the parent
 * bolt if every one of them lands, which needs a body on nearly every bearing — the payout is
 * gated on the crowd being there, not on the rank being bought.
 */
const SPLIT_DAMAGE_PCT = 35;
/** Total fan the shards are laid across, centred on the parent's heading. */
const SPLIT_FAN_DEG = 140;
const SPLIT_SPEED_MULT = 0.7;
/** 0.35 s at 70% of a lance's 52 u/s is ~12 u of reach: a burst, not a second volley. */
const SPLIT_DURATION_S = 0.35;
/**
 * Per-tick ceiling on shards from the whole build. A Krillscatter volley is 12 pellets and
 * they can all connect on the same tick, so without this one frame could ask for 48 bodies.
 * 32 covers two full weapons' worth of bursts and leaves the rest of the frame to the guns.
 */
const SPLIT_TICK_BUDGET = 32;
/**
 * §14.1 B3 — shards stop spawning at 85% of the projectile budget. The last 15% belongs to
 * primary weapon fire, which is the thing the player is aiming; a boon that eats those slots
 * would trade visible DPS for invisible DPS and read as the guns jamming.
 */
const SPLIT_HEADROOM_PCT = 85;

/** §6.6 — link reach of one cascade, measured from the impact point. Matches the §6.3.7 whip. */
const CASCADE_LINK_U = 9;
/** §6.6 — the 16 · 8 · 4 · 2 · 1 run: each level takes half of the one before it. */
const CASCADE_FALLOFF = 0.5;

/** Shards refused this tick, for the dev overlay and the acceptance tests. */
let splitTrimmed = 0;
let splitBudgetLeft = SPLIT_TICK_BUDGET;

/** Total shards the budget guards refused since the last `resetProjectiles`. */
export function splitTrimmedCount(): number {
  return splitTrimmed;
}

/**
 * Scratch contact for a cascade node. Separate from `bolt` because a cascade resolves *inside*
 * `onProjectileHit`, while `bolt` is still describing the strike that triggered it.
 */
const arc: HitContact = { x: 0, y: 0, dir_x: 0, dir_y: 0 };

/**
 * §6.6 — the cascade: a widening ripple out of one impact.
 *
 * Level k strikes the k next-nearest bodies for `0.5^k` of the bolt's snapshot, so with the
 * triggering hit read as 16 the run is 16 · 8 · 4 · 2 · 1 and the fan is 1 · 2 · 3 · 4 wide.
 * Total bodies at rank 4 is 1+2+3+4 = 10, which is why this is a ripple rather than a tree:
 * a branch-per-node reading of the same sequence is 1·2·3·4 = 24 leaves and 33 damage events
 * per bolt, and that composes with split into the geometric case the budget cannot absorb.
 *
 * §5.8 — every node is checked against the on-screen ring, so a cascade cannot walk itself off
 * the frame. AMD-24 — every distance is the short way round and every contact point is
 * canonicalised, so a ripple that opens on the seam still finds the bodies on the other side.
 */
function resolveCascade(w: World, p: Projectile, seed: Enemy, hx: number, hy: number, levels: number): void {
  let want = (levels * (levels + 1)) / 2;
  if (want > CASCADE_MAX_NODES) want = CASCADE_MAX_NODES;
  const n = w.hash.queryCircle(hx, hy, CASCADE_LINK_U + HIT_PAD, areaBuf, areaBuf.length);
  const stamp = w.nextStamp();
  const r2 = CASCADE_LINK_U * CASCADE_LINK_U;
  let kept = 0;
  for (let i = 0; i < n; i++) {
    const pi = areaBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    // The body that triggered the cascade already took the bolt; the ripple starts past it.
    if (e.id === seed.id) continue;
    if (!isWithinVisibleRange(w, e.pos.x, e.pos.y)) continue;
    const dx = wrapDeltaX(e.pos.x - hx);
    const dy = wrapDeltaY(e.pos.y - hy);
    const d2 = dx * dx + dy * dy;
    if (d2 > r2) continue;
    // Bounded insertion sort, nearest first, ties on lowest entity id (§5.9).
    if (kept >= want) {
      const worstId = w.enemies.slots[cascPi[want - 1]].id;
      if (d2 > cascD2[want - 1]) continue;
      if (d2 === cascD2[want - 1] && e.id >= worstId) continue;
    }
    let k = kept < want ? kept : want - 1;
    while (k > 0 && (cascD2[k - 1] > d2 || (cascD2[k - 1] === d2 && w.enemies.slots[cascPi[k - 1]].id > e.id))) {
      cascD2[k] = cascD2[k - 1];
      cascPi[k] = cascPi[k - 1];
      k--;
    }
    cascD2[k] = d2;
    cascPi[k] = pi;
    if (kept < want) kept++;
  }
  if (kept === 0) return;

  let idx = 0;
  let dmg = p.damage_snapshot;
  for (let level = 1; level <= levels && idx < kept; level++) {
    dmg *= CASCADE_FALLOFF;
    for (let j = 0; j < level && idx < kept; j++, idx++) {
      const e = w.enemies.slots[cascPi[idx]];
      if (!isTargetable(e)) continue;
      // A hop has a real arrival point: the rim of the struck body, facing where the arc came
      // from. Presentation throws the spall back along the link instead of centring the beat.
      const dx = wrapDeltaX(e.pos.x - hx);
      const dy = wrapDeltaY(e.pos.y - hy);
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      arc.dir_x = dx / d;
      arc.dir_y = dy / d;
      arc.x = wrapX(e.pos.x - arc.dir_x * e.hitbox_radius);
      arc.y = wrapY(e.pos.y - arc.dir_y * e.hitbox_radius);
      // depth 1: a cascade node is a proc off a strike, so §4.7's proc chain sees it as one
      // level down and nothing it kills can start a third. `canCrit` is false because the
      // trigger already took its `crit`-stream roll and the cascade is a share of that hit.
      dealDamage(w, e, dmg, p.weapon_id, false, p.owner_id, 1, arc);
      spawnChainSegment(w, p.weapon_id, e.pos.x, e.pos.y);
    }
  }
}

/**
 * §6.6 — the split: a spent bolt bursts into shards.
 *
 * Fires only where the bolt is actually consumed, which in §4.2 order is after penetration is
 * exhausted AND ricochet has declined. That placement is the whole design: split never
 * composes multiplicatively with either, because a bolt that still pierces has not died and a
 * bolt that bounces is still alive. Pierce → bounce → burst, one burst per bolt, ever.
 */
function spawnShards(w: World, p: Projectile, host: Enemy, hx: number, hy: number): void {
  const want = pSplit[p.pool_index];
  pSplit[p.pool_index] = 0;
  if (want <= 0) return;

  const ceiling = Math.floor((cfg.get('budget.max_projectiles') * SPLIT_HEADROOM_PCT) / 100);
  let allow = want;
  if (allow > splitBudgetLeft) allow = splitBudgetLeft;
  const headroom = ceiling - w.projectiles.count;
  if (allow > headroom) allow = headroom;
  if (allow < 0) allow = 0;
  if (allow < want) {
    // NOT `pool_exhausted`: nothing was denied at the pool, this system stood down on purpose.
    // The player is told a burst was clipped rather than left to wonder why a rank stopped
    // paying out (§14.1 B3 asks for deterministic denial, §13 asks for a legible one).
    w.bus.emit('budget_trim', { source: 'split', weapon_id: p.weapon_id, requested: want, spawned: allow });
    splitTrimmed += want - allow;
  }
  if (allow <= 0) return;
  splitBudgetLeft -= allow;

  const sp = Math.sqrt(p.vel.x * p.vel.x + p.vel.y * p.vel.y);
  const base = sp > 1e-6 ? Math.atan2(p.vel.y, p.vel.x) : p.phase;
  // The fan is laid out over the count the build asked for, not the count that survived the
  // budget, so a trimmed burst keeps its shape: shard 0 leaves on the same bearing either way.
  const step = want > 1 ? ((SPLIT_FAN_DEG * Math.PI) / 180) / (want - 1) : 0;

  resetSpawnStats();
  spawnStats.damage = p.damage_snapshot * (SPLIT_DAMAGE_PCT / 100);
  spawnStats.crit = false;
  spawnStats.speed = (sp > 1e-6 ? sp : 20) * SPLIT_SPEED_MULT;
  spawnStats.radius = 0.28;
  spawnStats.penetration = 1;
  spawnStats.duration_s = SPLIT_DURATION_S;
  spawnStats.behaviour = Behaviour.Linear;
  spawnStats.visual_class = p.visual_class;
  spawnStats.slot = pSlot[p.pool_index];
  spawnStats.knockback = p.knockback_snapshot * 0.5;
  for (let i = 0; i < allow; i++) {
    const a = base + (i - (want - 1) / 2) * step;
    spawnStats.phase = a;
    // AMD-24 — (hx, hy) is the canonicalised contact point; `spawnProjectile` wraps again, so
    // a bolt spent on the seam scatters onto the torus rather than off the domain.
    const shard = spawnProjectile(w, p.weapon_id, hx, hy, Math.cos(a), Math.sin(a), spawnStats, p.owner_id);
    if (shard === null) break;
    // A shard leaves from the rim of the body that stopped its parent, so the inward half of
    // the fan is born already touching it. Letting those re-strike would pay the burst back
    // into the target it just spent itself on — the boon is meant to spread damage off a body,
    // not to double-dip it — and it would eat those shards on the tick they were made.
    markHit(shard, host.id, 0x7fffffff);
  }
}

/** Returns false when the projectile was consumed (released or converted). */
function onProjectileHit(w: World, p: Projectile, e: Enemy, t: number): boolean {
  // Back-track from the (already canonicalised) end point to the contact point; the back-track
  // can step off the domain when the tick crossed the seam, so canonicalise it too.
  const hx = wrapX(p.pos.x - p.vel.x * STEP_S * (1 - t));
  const hy = wrapY(p.pos.y - p.vel.y * STEP_S * (1 - t));
  markHit(p, e.id, 0x7fffffff);

  const sp = Math.sqrt(p.vel.x * p.vel.x + p.vel.y * p.vel.y) || 1;
  // The one shape that knows where it landed: the sweep resolved the contact point, and the
  // heading is the bolt's own. Presentation puts the beat there on a body too big for a
  // centred flash to cover, and throws the spall along this vector.
  bolt.x = hx;
  bolt.y = hy;
  bolt.dir_x = p.vel.x / sp;
  bolt.dir_y = p.vel.y / sp;
  dealDamage(w, e, p.damage_snapshot, p.weapon_id, !p.crit_snapshot, p.owner_id, 0, bolt);
  applyKnockbackToEnemy(w, e, p.vel.x, p.vel.y, p.knockback_snapshot);
  if (p.status_on_hit !== '') applyStatus(w, e, p.status_on_hit, 1, p.owner_id, 0);

  const rt = runtimeOf(w, p);
  // §6.3.1 evolution — a crit lance fragments into homing barbs.
  if (rt !== null && rt.evolved && rt.evo_barbs > 0 && p.crit_snapshot && p.behaviour === Behaviour.Linear) {
    spawnBarbs(w, p, rt, hx, hy);
  }

  // §6.6 — the cascade spends on the FIRST body this bolt strikes, before penetration is
  // decremented, so a piercing bolt cascades once at the head of the line rather than once
  // per body in it. `pCascade` is cleared here, which is the whole rate limit: the charge is
  // handed to one projectile per volley in `weapons.ts`, so a 12-pellet fan cascades once.
  const cascade = pCascade[p.pool_index];
  if (cascade > 0) {
    pCascade[p.pool_index] = 0;
    resolveCascade(w, p, e, hx, hy, cascade);
  }

  if (p.penetration_left > 0) p.penetration_left--;
  if (p.penetration_left === 0) {
    // §4.2 — a projectile either penetrates or ricochets per hit; penetration resolves first.
    if (p.ricochet_left > 0) {
      const next = nearestExcluding(w, hx, hy, rt !== null && rt.evo_barb_range > 0 ? rt.evo_barb_range : 8, p);
      if (next !== null) {
        p.ricochet_left--;
        p.penetration_left = 1;
        const dx = wrapDeltaX(next.pos.x - hx);
        const dy = wrapDeltaY(next.pos.y - hy);
        const d = Math.sqrt(dx * dx + dy * dy) || 1;
        p.pos.x = hx;
        p.pos.y = hy;
        p.vel.x = (dx / d) * sp;
        p.vel.y = (dy / d) * sp;
        if (rt !== null && rt.evo_ricochet_pct > 0) p.damage_snapshot *= rt.evo_ricochet_pct / 100;
        p.behaviour = Behaviour.Ricochet;
        return true;
      }
    }
    // §6.6 — third and last branch of the §4.2 resolution: pierce, else bounce, else burst.
    if (pSplit[p.pool_index] > 0) spawnShards(w, p, e, hx, hy);
    w.projectiles.release(p);
    return false;
  }
  return true;
}

function nearestExcluding(w: World, x: number, y: number, range: number, p: Projectile): Enemy | null {
  const n = w.hash.queryCircle(x, y, range + HIT_PAD, areaBuf, areaBuf.length);
  const stamp = w.nextStamp();
  let best: Enemy | null = null;
  let bestD2 = Infinity;
  let bestId = 0;
  const r2 = range * range;
  for (let i = 0; i < n; i++) {
    const pi = areaBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    if (alreadyHit(p, e.id, w.tick)) continue;
    // §5.8 — the bounce chooses a new target, so it stays inside the on-screen ring. The
    // projectile itself is never cancelled here: with no visible candidate it simply does
    // not ricochet, exactly as if the field were empty.
    if (!isWithinVisibleRange(w, e.pos.x, e.pos.y)) continue;
    // §4.2 — a ricochet may bounce across the seam onto the nearest image of its next target.
    const dx = wrapDeltaX(e.pos.x - x);
    const dy = wrapDeltaY(e.pos.y - y);
    const d2 = dx * dx + dy * dy;
    if (d2 > r2) continue;
    if (d2 < bestD2 - 1e-9 || (Math.abs(d2 - bestD2) <= 1e-9 && e.id < bestId)) {
      bestD2 = d2;
      best = e;
      bestId = e.id;
    }
  }
  return best;
}

function spawnBarbs(w: World, parent: Projectile, rt: WeaponRuntime, x: number, y: number): void {
  const count = rt.evo_barbs | 0;
  const dmg = parent.damage_snapshot * (rt.evo_barb_damage_pct / 100);
  const step = (Math.PI * 2) / (count > 0 ? count : 1);
  for (let i = 0; i < count; i++) {
    const a = parent.phase + step * i;
    resetSpawnStats();
    spawnStats.damage = dmg;
    spawnStats.crit = false;
    spawnStats.speed = rt.speed * 0.8;
    spawnStats.radius = 0.3;
    spawnStats.penetration = 1;
    spawnStats.duration_s = 1.2;
    spawnStats.behaviour = Behaviour.Homing;
    spawnStats.visual_class = parent.visual_class;
    spawnStats.homing = 8;
    spawnStats.slot = pSlot[parent.pool_index];
    spawnStats.phase = a;
    const target = acquireTargetByCode(w, POLICY_NEAREST, x, y, rt.evo_barb_range, 0, 1, 0);
    spawnStats.target_id = target;
    spawnProjectile(w, parent.weapon_id, x, y, Math.cos(a), Math.sin(a), spawnStats, parent.owner_id);
  }
}

function stepHoming(w: World, p: Projectile): void {
  let target = enemyByEntityId(w, p.target_id);
  if (target === null || !isTargetable(target)) {
    const id = acquireTargetByCode(w, POLICY_NEAREST, p.pos.x, p.pos.y, 12, 0, 1, 0);
    p.target_id = id;
    target = id !== 0 ? enemyByEntityId(w, id) : null;
  }
  if (target !== null) {
    const sp = Math.sqrt(p.vel.x * p.vel.x + p.vel.y * p.vel.y) || 1;
    // Steer toward the nearest image: a barb behind the seam turns through it, not back across
    // the whole arena.
    const dx = wrapDeltaX(target.pos.x - p.pos.x);
    const dy = wrapDeltaY(target.pos.y - p.pos.y);
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    const k = p.homing_strength * STEP_S;
    let vx = p.vel.x / sp + (dx / d) * k;
    let vy = p.vel.y / sp + (dy / d) * k;
    const vl = Math.sqrt(vx * vx + vy * vy) || 1;
    vx /= vl;
    vy /= vl;
    p.vel.x = vx * sp;
    p.vel.y = vy * sp;
    p.phase = Math.atan2(vy, vx);
  }
  stepMoving(w, p);
}

function stepOrbital(w: World, p: Projectile): void {
  const rt = runtimeOf(w, p);
  const owner = w.player;
  const angular = ((rt !== null ? rt.angular_speed : 180) * Math.PI) / 180;
  p.phase += angular * STEP_S;
  if (p.phase > Math.PI * 2) p.phase -= Math.PI * 2;
  // The orbit offset is local to the owner; the absolute position it lands on must be wrapped.
  p.pos.x = owner.pos.x + Math.cos(p.phase) * p.orbit_radius;
  p.pos.y = owner.pos.y + Math.sin(p.phase) * p.orbit_radius;
  wrapPos(p.pos);

  const cooldownTicks = Math.max(1, Math.round((rt !== null ? rt.hit_cooldown_s : 0.6) * 60));
  const n = w.hash.queryCircle(p.pos.x, p.pos.y, p.hitbox_radius + HIT_PAD, areaBuf, areaBuf.length);
  const stamp = w.nextStamp();
  for (let i = 0; i < n; i++) {
    const pi = areaBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    const dx = wrapDeltaX(e.pos.x - p.pos.x);
    const dy = wrapDeltaY(e.pos.y - p.pos.y);
    const reach = p.hitbox_radius + e.hitbox_radius;
    if (dx * dx + dy * dy > reach * reach) continue;
    if (alreadyHit(p, e.id, w.tick)) continue;
    dealDamage(w, e, p.damage_snapshot, p.weapon_id, !p.crit_snapshot, p.owner_id, 0, null);
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    applyKnockbackToEnemy(w, e, dx, dy, p.knockback_snapshot);
    markHit(p, e.id, w.tick + cooldownTicks);
  }

  // §6.3.4 evolution — coronal flare across the full orbit radius. Only orbiter 0 fires it.
  if (rt !== null && rt.evolved && rt.evo_flare_interval_s > 0 && pSlotLeader(p)) {
    if (w.tick >= pTimer[p.pool_index]) {
      pTimer[p.pool_index] = w.tick + Math.max(1, Math.round(rt.evo_flare_interval_s * 60));
      const dmg = rt.evo_flare_damage * w.player.stats.damage_mult * w.player.lumensurge_tier;
      radialDamage(w, owner.pos.x, owner.pos.y, p.orbit_radius, dmg, p.weapon_id, false, p.owner_id, 0, 0);
    }
  }
}

/**
 * Leader flag: exactly one orbiter per slot owns shared pulses (the coronal flare).
 * `weapons.ts` sets `spawnStats.next_behaviour = LEADER_FLAG` on the first orbiter it spawns.
 */
export const LEADER_FLAG = 1;
function pSlotLeader(p: Projectile): boolean {
  return pNext[p.pool_index] === LEADER_FLAG;
}

function stepBeam(w: World, p: Projectile): void {
  const rt = runtimeOf(w, p);
  const owner = w.player;
  p.pos.x = owner.pos.x;
  p.pos.y = owner.pos.y;

  const rotating = p.homing_strength !== 0;
  if (rotating) {
    p.phase += p.homing_strength * STEP_S;
    if (p.phase > Math.PI * 2) p.phase -= Math.PI * 2;
    else if (p.phase < 0) p.phase += Math.PI * 2;
  } else {
    const target = enemyByEntityId(w, p.target_id);
    if (target !== null) {
      const want = Math.atan2(wrapDeltaY(target.pos.y - p.pos.y), wrapDeltaX(target.pos.x - p.pos.x));
      // §6.3.3 — sweep turn rate limits how fast the aperture tracks a new lock.
      const maxTurn = ((rt !== null && rt.speed > 0 ? 240 : 240) * Math.PI * STEP_S) / 180;
      let diff = want - p.phase;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      if (diff > maxTurn) diff = maxTurn;
      else if (diff < -maxTurn) diff = -maxTurn;
      p.phase += diff;
    }
  }

  if (w.tick < pTimer[p.pool_index]) return;
  const tickS = rt !== null && rt.contact_tick_s > 0 ? rt.contact_tick_s : rt !== null && rt.rate > 0 ? 1 / rt.rate : 0.2;
  pTimer[p.pool_index] = w.tick + Math.max(1, Math.round(tickS * 60));

  const length = p.orbit_radius;
  const halfWidth = p.hitbox_radius;
  // The aperture is a true displacement from the muzzle, so the end point is deliberately
  // unwrapped: that is what the toroidal DDA and `wrapSegDist2` both expect (AMD-24).
  const sx = Math.cos(p.phase) * length;
  const sy = Math.sin(p.phase) * length;
  const ex = p.pos.x + sx;
  const ey = p.pos.y + sy;
  const n = w.hash.querySegment(p.pos.x, p.pos.y, ex, ey, halfWidth + HIT_PAD, segBuf, segBuf.length);
  const stamp = w.nextStamp();

  // §6.3.3 evolution — held-target damage ramp, reset on retarget.
  let ramp = 1;
  if (rt !== null && rt.evolved && rt.evo_ramp_pct_per_s > 0 && !rotating) {
    pAux[p.pool_index] += tickS;
    const cap = rt.evo_ramp_cap_pct / 100;
    const r = (pAux[p.pool_index] * rt.evo_ramp_pct_per_s) / 100;
    ramp = 1 + (r > cap ? cap : r);
  }

  const maxTargets = p.penetration_left < 0 ? 0x7fffffff : p.penetration_left;
  let struck = 0;
  const focus = w.player.focus_aim;
  const focusCos = Math.cos((30 * Math.PI) / 180);
  for (let i = 0; i < n; i++) {
    if (struck >= maxTargets) break;
    const pi = segBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    const reach = halfWidth + e.hitbox_radius;
    if (wrapSegDist2(p.pos.x, p.pos.y, sx, sy, e.pos.x, e.pos.y) > reach * reach) continue;
    let dmg = p.damage_snapshot * ramp;
    if (struck > 0 && rt !== null && rt.secondary_pct > 0) dmg *= rt.secondary_pct / 100;
    // §6.3.12 evolution — Focus-Aim cone superheat.
    if (rt !== null && rt.evolved && rt.evo_focus_bonus_pct > 0 && focus.held) {
      const dx = wrapDeltaX(e.pos.x - p.pos.x);
      const dy = wrapDeltaY(e.pos.y - p.pos.y);
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      if ((dx / d) * focus.dir.x + (dy / d) * focus.dir.y >= focusCos) dmg *= 1 + rt.evo_focus_bonus_pct / 100;
    }
    dealDamage(w, e, dmg, p.weapon_id, !p.crit_snapshot, p.owner_id, 0, null);
    if (rt !== null && rt.evolved) {
      // §6.3.3 evolution — Burn, magnitude derived from the beam tick (§4.9 weapon-derived).
      if (rt.evo_burn_pct > 0) {
        applyStatus(w, e, 'status.burn', 1, p.owner_id, (dmg * rt.evo_burn_pct) / 100);
      }
      // §6.3.12 evolution — Eclipse mark (weaken family, capped at +25% by §04 step 3).
      if (rt.evo_eclipse_pct > 0) {
        applyWeakenFamily(w, e, rt.evo_eclipse_pct, p.owner_id);
      }
    }
    struck++;
  }
}

function stepLobbed(w: World, p: Projectile): void {
  const i = p.pool_index;
  // The lob takes the short way to its anchor, which may run across the seam.
  const dx = wrapDeltaX(pDestX[i] - p.pos.x);
  const dy = wrapDeltaY(pDestY[i] - p.pos.y);
  const d = Math.sqrt(dx * dx + dy * dy);
  const step = Math.sqrt(p.vel.x * p.vel.x + p.vel.y * p.vel.y) * STEP_S;
  if (d <= step || d < 1e-4) {
    p.pos.x = pDestX[i];
    p.pos.y = pDestY[i];
    p.vel.x = 0;
    p.vel.y = 0;
    p.behaviour = pNext[i];
    const rt = runtimeOf(w, p);
    if (p.behaviour === Behaviour.Mine) {
      pArm[i] = w.tick + Math.max(1, Math.round((rt !== null ? rt.arm_time_s : 0.8) * 60));
    } else if (p.behaviour === Behaviour.Zone) {
      pTimer[i] = w.tick + 60;
      mergeZones(w, p, rt);
    }
    return;
  }
  p.pos.x += (dx / d) * step;
  p.pos.y += (dy / d) * step;
  wrapPos(p.pos);
}

/** §6.3.9 evolution — overlapping pools merge; +50% radius per merge, capped ×2.5. */
function mergeZones(w: World, p: Projectile, rt: WeaponRuntime | null): void {
  if (rt === null || !rt.evolved || rt.evo_merge_bonus_pct <= 0) return;
  const list = w.projectiles.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const q = w.projectiles.slots[list[i]];
    if (q.pool_index === p.pool_index) continue;
    if (q.behaviour !== Behaviour.Zone || q.weapon_id !== p.weapon_id) continue;
    const dx = wrapDeltaX(q.pos.x - p.pos.x);
    const dy = wrapDeltaY(q.pos.y - p.pos.y);
    const reach = q.area_snapshot + p.area_snapshot;
    if (dx * dx + dy * dy > reach * reach) continue;
    const cap = rt.radius * rt.evo_merge_cap_mult;
    let grown = q.area_snapshot * (1 + rt.evo_merge_bonus_pct / 100);
    if (grown > cap) grown = cap;
    q.area_snapshot = grown;
    if (p.expire_tick > q.expire_tick) q.expire_tick = p.expire_tick;
    w.projectiles.release(p);
    return;
  }
}

function stepZone(w: World, p: Projectile): void {
  const rt = runtimeOf(w, p);
  if (w.tick < pTimer[p.pool_index]) return;
  pTimer[p.pool_index] = w.tick + 60; // §6.3.9 acceptance — 1 Hz tick

  const radius = p.area_snapshot;
  const n = w.hash.queryCircle(p.pos.x, p.pos.y, radius + HIT_PAD, areaBuf, areaBuf.length);
  const stamp = w.nextStamp();
  const r2 = radius * radius;
  for (let i = 0; i < n; i++) {
    const pi = areaBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    const dx = wrapDeltaX(e.pos.x - p.pos.x);
    const dy = wrapDeltaY(e.pos.y - p.pos.y);
    if (dx * dx + dy * dy > r2) continue;
    dealDamage(w, e, p.damage_snapshot, p.weapon_id, !p.crit_snapshot, p.owner_id, 0, null);
    if (rt !== null && rt.slow_pct > 0) {
      applyStatus(w, e, 'status.slow', 5, p.owner_id, rt.slow_pct / 100 / 5);
    }
    // §6.3.9 evolution — enemies inside a lake take +15% from all sources (applies once).
    if (rt !== null && rt.evolved && rt.evo_amp_pct > 0) {
      applyWeakenFamily(w, e, rt.evo_amp_pct, p.owner_id);
    }
  }

  // §6.3.9 evolution — the player regenerates while standing in their own lake.
  if (rt !== null && rt.evolved && rt.evo_player_regen > 0) {
    const dx = wrapDeltaX(w.player.pos.x - p.pos.x);
    const dy = wrapDeltaY(w.player.pos.y - p.pos.y);
    if (dx * dx + dy * dy <= r2) {
      const heal = Math.min(rt.evo_player_regen, w.player.max_hp - w.player.hp);
      if (heal > 0) {
        w.player.hp += heal;
        w.bus.emit('player_heal', { amount: heal, hp_after: w.player.hp });
      }
    }
  }
}

function stepMine(w: World, p: Projectile): void {
  const i = p.pool_index;
  if (w.tick < pArm[i]) return;
  const rt = runtimeOf(w, p);
  const trigger = rt !== null && rt.trigger_radius > 0 ? rt.trigger_radius : p.hitbox_radius;
  const n = w.hash.queryCircle(p.pos.x, p.pos.y, trigger + HIT_PAD, areaBuf, areaBuf.length);
  const stamp = w.nextStamp();
  const t2 = trigger * trigger;
  let triggered = false;
  for (let k = 0; k < n; k++) {
    const pi = areaBuf[k];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    const dx = wrapDeltaX(e.pos.x - p.pos.x);
    const dy = wrapDeltaY(e.pos.y - p.pos.y);
    if (dx * dx + dy * dy > t2) continue;
    triggered = true;
    break;
  }
  if (!triggered) return;
  detonateMine(w, p, rt);
}

function detonateMine(w: World, p: Projectile, rt: WeaponRuntime | null): void {
  radialDamage(
    w,
    p.pos.x,
    p.pos.y,
    p.area_snapshot,
    p.damage_snapshot,
    p.weapon_id,
    !p.crit_snapshot,
    p.owner_id,
    p.knockback_snapshot,
    0
  );
  // §6.3.6 evolution — scatter micro-mines and light the lure.
  if (rt !== null && rt.evolved) {
    if (rt.evo_taunt_radius > 0) applyTaunt(w, p.pos.x, p.pos.y, rt.evo_taunt_radius, rt.evo_taunt_duration_s);
    const count = rt.evo_micro_mines | 0;
    if (count > 0 && p.penetration_left !== -2) {
      const step = (Math.PI * 2) / count;
      const spread = p.area_snapshot * 0.6;
      for (let i = 0; i < count; i++) {
        const a = p.phase + step * i;
        resetSpawnStats();
        spawnStats.damage = p.damage_snapshot * (rt.evo_micro_damage_pct / 100);
        spawnStats.crit = false;
        spawnStats.speed = 12;
        spawnStats.radius = 0.4;
        spawnStats.area = rt.evo_micro_radius;
        spawnStats.penetration = -2; // marks a micro-mine: never scatters again
        spawnStats.duration_s = 8;
        spawnStats.behaviour = Behaviour.Lobbed;
        spawnStats.next_behaviour = Behaviour.Mine;
        spawnStats.visual_class = p.visual_class;
        spawnStats.slot = pSlot[p.pool_index];
        spawnStats.arm_s = 0.4;
        spawnStats.dest_x = p.pos.x + Math.cos(a) * spread;
        spawnStats.dest_y = p.pos.y + Math.sin(a) * spread;
        spawnStats.knockback = p.knockback_snapshot;
        spawnProjectile(w, p.weapon_id, p.pos.x, p.pos.y, Math.cos(a), Math.sin(a), spawnStats, p.owner_id);
      }
    }
  }
  w.projectiles.release(p);
}

function stepMinion(w: World, p: Projectile): void {
  const i = p.pool_index;
  const rt = runtimeOf(w, p);
  const speed = rt !== null && rt.speed > 0 ? rt.speed : 12;
  let target = enemyByEntityId(w, p.target_id);
  if (target === null || !isTargetable(target)) {
    const id = acquireTargetByCode(w, POLICY_NEAREST, p.pos.x, p.pos.y, MAX_ACQUISITION_RANGE, 0, 1, 0);
    p.target_id = id;
    target = id !== 0 ? enemyByEntityId(w, id) : null;
  }

  // A scion shadows its target and dives on cadence rather than returning to the player
  // between dives — the §6.3.8 dive cooldown is what sets its DPS, not travel time.
  let gx = w.player.pos.x;
  let gy = w.player.pos.y;
  if (target !== null) {
    gx = target.pos.x;
    gy = target.pos.y;
  }
  const dx = wrapDeltaX(gx - p.pos.x);
  const dy = wrapDeltaY(gy - p.pos.y);
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d > 1e-4) {
    const step = Math.min(speed * STEP_S, d);
    p.pos.x += (dx / d) * step;
    p.pos.y += (dy / d) * step;
    wrapPos(p.pos);
    p.vel.x = (dx / d) * speed;
    p.vel.y = (dy / d) * speed;
    p.phase = Math.atan2(dy, dx);
  }

  if (target === null || w.tick < pArm[i]) return;
  const reach = p.hitbox_radius + target.hitbox_radius;
  const tdx = wrapDeltaX(target.pos.x - p.pos.x);
  const tdy = wrapDeltaY(target.pos.y - p.pos.y);
  if (tdx * tdx + tdy * tdy > reach * reach) return;

  const volley = pAux[i] > 0;
  let dmg = p.damage_snapshot;
  if (volley && rt !== null) {
    dmg *= rt.evo_volley_damage_pct / 100;
    pAux[i] = 0;
  }
  dealDamage(w, target, dmg, p.weapon_id, !p.crit_snapshot, p.owner_id, 0, null);
  const dive = rt !== null && rt.rate > 0 ? 1 / rt.rate : 1.1;
  pArm[i] = w.tick + Math.max(1, Math.round(dive * 60));
}

function stepAura(w: World, p: Projectile): void {
  const i = p.pool_index;
  if (pAux[i] !== 0) return; // already resolved; remaining ticks are the expanding-ring visual
  pAux[i] = 1;
  const rt = runtimeOf(w, p);
  const rings = rt !== null && rt.evolved && rt.evo_rings > 1 ? rt.evo_rings | 0 : 1;
  const radius = p.area_snapshot;
  if (rings <= 1) {
    radialDamage(
      w,
      p.pos.x,
      p.pos.y,
      radius,
      p.damage_snapshot,
      p.weapon_id,
      !p.crit_snapshot,
      p.owner_id,
      p.knockback_snapshot,
      0
    );
    return;
  }
  // §6.3.5 evolution — concentric rings resolve inner-to-outer in one sim tick; an enemy is
  // struck by the innermost ring that contains it, so damage falls off with distance.
  const n = w.hash.queryCircle(p.pos.x, p.pos.y, radius + HIT_PAD, areaBuf, areaBuf.length);
  const stamp = w.nextStamp();
  for (let k = 0; k < n; k++) {
    const pi = areaBuf[k];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    const dx = wrapDeltaX(e.pos.x - p.pos.x);
    const dy = wrapDeltaY(e.pos.y - p.pos.y);
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d > radius + e.hitbox_radius) continue;
    let ring = Math.floor((d / radius) * rings);
    if (ring >= rings) ring = rings - 1;
    if (ring < 0) ring = 0;
    const mult = rt !== null ? rt.evo_ring_mult[ring] : 1;
    dealDamage(w, e, p.damage_snapshot * mult, p.weapon_id, !p.crit_snapshot, p.owner_id, 0, null);
    const dd = d || 1;
    applyKnockbackToEnemy(w, e, dx, dy, p.knockback_snapshot);
    if (rt !== null && rt.evo_mark_pct > 0) {
      applyWeakenFamily(w, e, rt.evo_mark_pct, p.owner_id);
    }
  }
}

/** §6.3.7 evolution — struck enemies become re-arcing nodes for `node_duration_s`. */
function stepChainNode(w: World, p: Projectile): void {
  const i = p.pool_index;
  if (p.target_id === 0) return; // plain visual segment
  const host = enemyByEntityId(w, p.target_id);
  if (host === null) {
    w.projectiles.release(p);
    return;
  }
  p.pos.x = host.pos.x;
  p.pos.y = host.pos.y;
  if (w.tick < pTimer[i]) return;
  pTimer[i] = w.tick + 60;
  const rt = runtimeOf(w, p);
  if (rt === null) return;
  const next = nearestExcluding(w, p.pos.x, p.pos.y, rt.radius > 0 ? rt.radius : 10, p);
  if (next === null) return;
  markHit(p, next.id, 0x7fffffff);
  resolveChain(
    w,
    next.id,
    p.damage_snapshot,
    rt.evo_node_retargets,
    rt.chain_targets > 0 ? rt.range : 10,
    rt.chain_falloff,
    p.weapon_id,
    !p.crit_snapshot,
    p.owner_id,
    host.id,
    1
  );
}

// --- step 9 ---------------------------------------------------------------------------------------

/** Reset all projectile side state (world reset). */
export function resetProjectiles(): void {
  pSlot.fill(0);
  pTimer.fill(0);
  pArm.fill(0);
  pNext.fill(0);
  pAux.fill(0);
  pDestX.fill(0);
  pDestY.fill(0);
  pHitTick.fill(0);
  pSplit.fill(0);
  pCascade.fill(0);
  splitTrimmed = 0;
  splitBudgetLeft = SPLIT_TICK_BUDGET;
  tauntCount = 0;
}

/** §6.3.3 evolution — clear a beam's held-target damage ramp (called on retarget). */
export function resetBeamRamp(p: Projectile): void {
  pAux[p.pool_index] = 0;
}

/** Mark a minion for the next synchronized volley (called by weapons.ts). */
export function armVolley(p: Projectile): void {
  pAux[p.pool_index] = 1;
}

/** Force a mine to detonate (used by the lure / cleanup paths). */
export function forceDetonate(w: World, p: Projectile): void {
  detonateMine(w, p, runtimeOf(w, p));
}

/**
 * Step 9 — integrate every live projectile, resolve collisions, expire.
 * Iteration is ascending pool index; releases during the pass are safe because the live list
 * is snapshotted before the loop and every slot re-checks `alive`.
 */
export function stepProjectiles(w: World): void {
  // Only the taunt registry needs clearing at run start: every other side-state slot is fully
  // rewritten by `spawnProjectile`, and a blanket reset here would clobber shapes that
  // `stepWeapons` already spawned earlier in this same tick.
  if (w.tick === 0) tauntCount = 0;
  // §6.6 — the shard allowance is a per-frame budget, so it refills here and nowhere else.
  splitBudgetLeft = SPLIT_TICK_BUDGET;
  rebuildEnemyIndex(w);
  stepTaunts(w);

  const list = w.projectiles.liveIndices();
  const n = list.length;
  for (let i = 0; i < n; i++) {
    const p = w.projectiles.slots[list[i]];
    if (!p.alive) continue;
    // Enemy-faction shapes belong to R3 (`enemies.ts`) / R4 (`boss.ts`); they share the pool
    // but carry their own behaviour ids and integration, so this step never touches them.
    if (p.faction !== 'player') continue;
    if (w.tick >= p.expire_tick) {
      // §6.3.6 — a mine that outlives its lifetime disarms silently rather than detonating.
      w.projectiles.release(p);
      continue;
    }
    switch (p.behaviour) {
      case Behaviour.Linear:
      case Behaviour.Ricochet:
        stepMoving(w, p);
        break;
      case Behaviour.Homing:
        stepHoming(w, p);
        break;
      case Behaviour.Orbital:
        stepOrbital(w, p);
        break;
      case Behaviour.Beam:
        stepBeam(w, p);
        break;
      case Behaviour.Lobbed:
        stepLobbed(w, p);
        break;
      case Behaviour.Zone:
        stepZone(w, p);
        break;
      case Behaviour.Chain:
        stepChainNode(w, p);
        break;
      case Behaviour.Minion:
        stepMinion(w, p);
        break;
      case Behaviour.Aura:
        stepAura(w, p);
        break;
      case Behaviour.Mine:
        stepMine(w, p);
        break;
      case Behaviour.Melee:
        break;
      default:
        break;
    }
  }
}

/** Exported for weapons.ts: the elite-priority policy code used by the armada volley. */
export const VOLLEY_POLICY = POLICY_ELITE_PRIORITY;
/** Exported for weapons.ts: shared aim helper output. */
export { aimAt, aimOut };
